import { useFlags } from 'launchdarkly-react-client-sdk'
import { createContext, ReactNode, useCallback, useMemo, useState } from 'react'

import {
  Notification,
  NotificationProps,
} from '~/src/components/shared/Notification/types'

import { NotificationType } from '~/__generated_types__/globalTypes'

export type NotificationContextType = {
  notifications: Array<Notification>
  isResolvedHidden: boolean
  isDropdownOpen: boolean
  update: (messages: Array<Notification>) => void
  hideResolvedNotifications: () => void
  setIsDropdownOpen: (isOpen: boolean) => void
}

export const NotificationContext = createContext<NotificationContextType | null>(null)

export const NotificationContextProvider = ({ children }: { children: ReactNode }) => {
  const [isResolvedHidden, setIsResolvedHidden] = useState(false)
  const [notifications, setNotifications] = useState<Array<NotificationProps>>([])
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  const { allowLateReasonForRv3 } = useFlags()

  const update = useCallback(
    (messages: Array<Notification>) => {
      let filteredMessages = messages

      if (!allowLateReasonForRv3) {
        filteredMessages = messages.filter(
          (notification) => notification.type !== NotificationType.LateAppointment
        )
      }

      setNotifications(filteredMessages)
    },
    [allowLateReasonForRv3]
  )

  const hideResolvedNotifications = useCallback(() => {
    setIsResolvedHidden((prev) => !prev)
  }, [])

  const value: NotificationContextType = useMemo(
    () => ({
      notifications,
      isDropdownOpen,
      isResolvedHidden,
      setIsDropdownOpen,
      hideResolvedNotifications,
      update,
    }),
    [
      isResolvedHidden,
      notifications,
      isDropdownOpen,
      update,
      setIsDropdownOpen,
      hideResolvedNotifications,
    ]
  )

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  )
}
